// Both Liquid and Storefront API records use this same filter/order pipeline.
export function scopedFaqs(records, config) {
  const categories = String(config.categoryFilter || '').split(',').map(s => s.trim()).filter(Boolean);
  return records.filter(r => r.question && (r.html || r.answer)
    && r.category?.handle !== 'unlisted'
    && (!categories.length || categories.includes(r.category?.id) || categories.includes(r.category?.handle))
    && (config.scope !== 'assigned' || r.products.length > 0)
    && (!['current', 'selected'].includes(config.scope) || (config.productFilter && r.products.includes(config.productFilter)))
    && (!config.productFilter || r.products.includes(config.productFilter)))
    .sort((a, b) => a.priority - b.priority || a.question.localeCompare(b.question));
}

export function normalizeFaq(node) {
  const fields = Object.fromEntries(node.fields.map(f => [f.key, f]));
  const ref = fields.categories?.reference;
  const categoryFields = Object.fromEntries((ref?.fields || []).map(f => [f.key, f.value]));
  let products = [];
  try { products = JSON.parse(fields.applicable_products?.value || '[]'); } catch { /* Empty invalid reference list. */ }
  return {
    id: node.id, question: fields.question?.value || '', answer: fields.answer?.value || '',
    priority: Number(fields.display_priority?.value) || 0, products,
    category: { id: ref?.id || fields.categories?.value || '', handle: ref?.handle || '',
      label: categoryFields.title || categoryFields.name || categoryFields.category || '',
      priority: Number(categoryFields.order ?? categoryFields.display_priority) || 0 }
  };
}

export async function fetchFaqs(config, signal, request = fetch) {
  const records = new Map();
  const cursors = new Set();
  let after = null;
  do {
    const response = await request(`/api/2026-01/graphql.json`, {
      method: 'POST', signal,
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': config.token },
      body: JSON.stringify({ query: `query FAQs($after: String) {
        metaobjects(type: "faqs", first: 50, after: $after) {
          nodes { id fields { key value reference { ... on Metaobject { id handle fields { key value } } } } }
          pageInfo { hasNextPage endCursor }
        }
      }`, variables: { after } })
    });
    if (!response.ok) throw new Error('FAQ request failed');
    const payload = await response.json();
    if (payload.errors?.length || !payload.data?.metaobjects) throw new Error('FAQ data unavailable');
    const connection = payload.data.metaobjects;
    connection.nodes.forEach(node => records.set(node.id, normalizeFaq(node)));
    if (!connection.pageInfo.hasNextPage) break;
    after = connection.pageInfo.endCursor;
    if (!after || cursors.has(after)) throw new Error('FAQ pagination did not advance');
    cursors.add(after);
  } while (after);
  return [...records.values()];
}

// Shopify rich text is rendered with a small DOM allowlist, never arbitrary HTML.
export function richTextNode(node, doc = document) {
  const fragment = doc.createDocumentFragment();
  if (!node || typeof node !== 'object') return fragment;
  if (node.type === 'text') {
    let result = doc.createTextNode(node.value || '');
    for (const [flag, tag] of [['bold', 'strong'], ['italic', 'em']]) {
      if (node[flag]) { const wrap = doc.createElement(tag); wrap.append(result); result = wrap; }
    }
    return result;
  }
  const tag = { paragraph: 'p', heading: `h${Math.min(6, Math.max(2, Number(node.level) || 3))}`,
    list: node.listType === 'ordered' ? 'ol' : 'ul', 'list-item': 'li', link: 'a' }[node.type];
  const element = tag ? doc.createElement(tag) : fragment;
  if (tag === 'a') {
    const url = String(node.url || '').trim();
    if (/^(https?:|mailto:|tel:|\/(?!\/)|#)/i.test(url)) element.setAttribute('href', url);
    if (node.target === '_blank') { element.target = '_blank'; element.rel = 'noopener noreferrer'; }
  }
  (node.children || []).forEach(child => element.append(richTextNode(child, doc)));
  return element;
}

class XomdFaqs extends (typeof HTMLElement === 'undefined' ? class {} : HTMLElement) {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.config = JSON.parse(this.querySelector('[data-faq-config]').textContent);
    this.records = JSON.parse(this.querySelector('[data-faq-records]').textContent);
    this.list = this.querySelector('[data-faq-list]');
    this.panel = this.querySelector('.xomd-faqs__panel');
    this.status = this.querySelector('[data-faq-status]');
    this.retry = this.querySelector('[data-faq-retry]');
    this.active = '';
    this.events = new AbortController();
    this.addEventListener('click', event => {
      const button = event.target.closest('[data-faq-category]');
      if (button && this.contains(button)) { this.active = button.dataset.faqCategory; this.render(false); }
      if (event.target.closest('[data-faq-retry]')) this.load();
    }, { signal: this.events.signal });
    this.render();
    if (this.config.fetchAll) this.load();
  }
  disconnectedCallback() { this.events?.abort(); this.request?.abort(); this.initialized = false; }
  async load() {
    this.request?.abort();
    const request = new AbortController();
    this.request = request;
    const timer = setTimeout(() => request.abort(), 20000);
    this.status.textContent = 'Loading all questions…';
    this.list.setAttribute('aria-busy', 'true');
    this.retry.hidden = true;
    try {
      if (!this.config.token) throw new Error('Missing public Storefront token');
      const records = await fetchFaqs(this.config, request.signal);
      if (request.signal.aborted || this.request !== request) return;
      // Liquid can resolve category labels even when an API token cannot.
      const categories = new Map(this.records.filter(r => r.category?.label).map(r => [r.category.id, r.category]));
      this.records = records.map(r => ({ ...r, category: r.category.label ? r.category : categories.get(r.category.id) || r.category }));
      this.render();
      this.status.textContent = '';
    } catch {
      if (this.isConnected && this.request === request) {
        this.status.textContent = 'Some questions could not be loaded. You can still browse the available questions.';
        this.retry.hidden = false;
      }
    } finally { clearTimeout(timer); if (this.request === request) this.list.setAttribute('aria-busy', 'false'); }
  }
  render(rebuildPanel = true) {
    const records = scopedFaqs(this.records, this.config);
    const groups = new Map();
    for (const record of records) {
      const key = this.config.group ? record.category?.id || 'general' : 'all';
      if (!groups.has(key)) groups.set(key, { key, label: record.category?.label || (record.products.length ? 'Products' : 'General questions'), priority: record.category?.label ? record.category.priority : 0.5, records: [] });
      groups.get(key).records.push(record);
    }
    const ordered = [...groups.values()].sort((a, b) => a.priority - b.priority || a.label.localeCompare(b.label));
    if (this.active && !groups.has(this.active)) this.active = '';
    const showPanel = this.config.group && this.config.panel && groups.size > 1;
    this.panel.hidden = !showPanel;
    this.classList.toggle('has-panel', showPanel);
    if (rebuildPanel) {
      this.panel.replaceChildren();
      for (const group of [{ key: '', label: 'All questions' }, ...ordered]) {
        const button = document.createElement('button');
        button.type = 'button'; button.dataset.faqCategory = group.key; button.textContent = group.label;
        this.panel.append(button);
      }
    }
    this.panel.querySelectorAll('button').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.faqCategory === this.active)));
    const openIds = new Set([...this.list.querySelectorAll('details[open]')].map(e => e.dataset.faqId));
    const content = document.createDocumentFragment();
    for (const group of ordered) {
      if (this.active && group.key !== this.active) continue;
      const container = document.createElement('div'); container.className = 'xomd-faqs__group';
      if (this.config.group) { const heading = document.createElement('h2'); heading.textContent = group.label; container.append(heading); }
      for (const record of group.records) {
        const details = document.createElement('details'); details.dataset.faqId = record.id; details.open = openIds.has(record.id);
        details.setAttribute('itemscope', ''); details.setAttribute('itemprop', 'mainEntity'); details.setAttribute('itemtype', 'https://schema.org/Question');
        const summary = document.createElement('summary'); summary.textContent = record.question;
        summary.setAttribute('itemprop', 'name');
        const body = document.createElement('div'); body.className = 'xomd-faqs__answer rte';
        body.setAttribute('itemprop', 'text');
        if (record.html) {
          // Only Liquid's metafield_tag output enters this branch; API content uses the allowlist above.
          const template = document.createElement('template'); template.innerHTML = record.html; body.append(template.content);
        } else {
          try { body.append(richTextNode(JSON.parse(record.answer))); } catch { body.textContent = record.answer; }
        }
        const answer = document.createElement('div');
        answer.setAttribute('itemscope', ''); answer.setAttribute('itemprop', 'acceptedAnswer'); answer.setAttribute('itemtype', 'https://schema.org/Answer');
        answer.append(body); details.append(summary, answer); container.append(details);
      }
      content.append(container);
    }
    if (!records.length) { const empty = document.createElement('p'); empty.textContent = 'No questions match these filters.'; content.append(empty); }
    this.list.replaceChildren(content);
  }
}
if (typeof customElements !== 'undefined' && !customElements.get('xomd-faqs')) customElements.define('xomd-faqs', XomdFaqs);
